import { useState, useEffect } from 'react';
import { tefApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from '../../hooks/use-toast';
import { Save, Loader2 } from 'lucide-react';

/** Datos del emisor Chirino (cuenta de cargo + RUT + motivo) — colección tef_config. */
export function TefConfigEmisorTab() {
  const [cfg, setCfg] = useState({ rut_emisor: '', cuenta_cargo: '', motivo: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await tefApi.getConfig();
      setCfg({
        rut_emisor: res.data.rut_emisor || '',
        cuenta_cargo: res.data.cuenta_cargo || '',
        motivo: res.data.motivo || '',
      });
    } catch {
      toast({ title: 'Error al cargar la configuración del emisor', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg.rut_emisor.trim() || !cfg.cuenta_cargo.trim()) {
      toast({ title: 'RUT emisor y cuenta de cargo son obligatorios', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await tefApi.putConfig(cfg);
      toast({ title: 'Configuración del emisor guardada' });
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Datos del emisor (Chirino Steel)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-lg">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="tef-rut-emisor">RUT emisor</Label>
              <Input
                id="tef-rut-emisor"
                value={cfg.rut_emisor}
                onChange={(e) => setCfg({ ...cfg, rut_emisor: e.target.value })}
                placeholder="76738421-1"
                data-testid="tef-emisor-rut"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tef-cuenta-cargo">Cuenta de cargo</Label>
              <Input
                id="tef-cuenta-cargo"
                value={cfg.cuenta_cargo}
                onChange={(e) => setCfg({ ...cfg, cuenta_cargo: e.target.value })}
                placeholder="9230159910"
                data-testid="tef-emisor-cuenta"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tef-motivo">Motivo (glosa)</Label>
              <Input
                id="tef-motivo"
                value={cfg.motivo}
                onChange={(e) => setCfg({ ...cfg, motivo: e.target.value })}
                placeholder="Chirino Steel Pago Factura"
                maxLength={30}
                data-testid="tef-emisor-motivo"
              />
              <p className="text-xs text-muted-foreground">Máx. 30 caracteres (campo del banco).</p>
            </div>
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="tef-emisor-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
